import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { organisationApi } from '../services/api';
import config from '../config';
import GoogleIdentityButton from './auth/GoogleIdentityButton';
import SequencePathBar from './SequencePathBar';
import { isValidEmail } from '../utils/emailValidation';
import './VenueExperience.css';
import './auth/LoginDesign.css';

const Register = () => {
  const location = useLocation();
  const handoff = location.state || {};
  const fromHire = Boolean(handoff.fromHire);

  const [formData, setFormData] = useState({
    displayName: '',
    email: handoff.verifiedEmail || handoff.prefilledEmail || '',
    password: '',
    confirmPassword: '',
    organisation_id: handoff.organisationId ? String(handoff.organisationId) : '',
  });
  const [organisations, setOrganisations] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [isReady, setIsReady] = useState(false);

  const { register, loginWithGoogle, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [googleLoading, setGoogleLoading] = useState(false);

  useEffect(() => {
    const rafId = window.requestAnimationFrame(() => setIsReady(true));
    return () => window.cancelAnimationFrame(rafId);
  }, []);

  useEffect(() => {
    if (isAuthenticated && user) {
      navigate('/app', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  // Fetch available organisations on mount
  useEffect(() => {
    const fetchOrganisations = async () => {
      try {
        // Public endpoint to list active organisations for registration
        const response = await organisationApi.getPublicList();
        setOrganisations(response.data?.data || []);
      } catch (err) {
        console.error('Failed to fetch organisations:', err);
        // Fallback for development
        setOrganisations([]);
      } finally {
        setLoadingOrgs(false);
      }
    };
    fetchOrganisations();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.displayName.trim()) {
      newErrors.displayName = 'Name is required';
    }
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (!formData.organisation_id) {
      newErrors.organisation_id = 'Please select a studio location';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    if (!validateForm()) return;
    setLoading(true);
    try {
      const result = await register({
        displayName: formData.displayName,
        email: formData.email,
        password: formData.password,
        organisation_id: parseInt(formData.organisation_id),
      });

      if (result?.pendingApproval) {
        setSuccessMessage(result.message || 'Account created and pending approval. We will email you when access is granted.');
        setFormData((prev) => ({
          ...prev,
          password: '',
          confirmPassword: '',
        }));
        return;
      }

      navigate('/app', { replace: true });
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid =
    formData.displayName &&
    formData.email &&
    isValidEmail(formData.email) &&
    formData.password &&
    formData.password.length >= 8 &&
    formData.password === formData.confirmPassword &&
    formData.organisation_id;

  const handleGoogleCredential = async (response) => {
    setError('');
    if (!formData.organisation_id) {
      setError('Please select a studio location before continuing with Google.');
      setErrors((prev) => ({ ...prev, organisation_id: 'Please select a studio location' }));
      return;
    }
    setGoogleLoading(true);
    try {
      const result = await loginWithGoogle({
        idToken: response.credential,
        mode: 'signup',
        organisationId: parseInt(formData.organisation_id, 10),
        displayName: formData.displayName || undefined,
      });
      if (result?.pendingApproval) {
        setSuccessMessage(result.message || 'Your account is waiting for admin approval. We will email you when access is granted.');
        return;
      }
      navigate('/app', { replace: true });
    } catch (err) {
      if (err?.pendingApproval) {
        setSuccessMessage(err.message || 'Your account is waiting for admin approval. We will email you when access is granted.');
      } else {
        setError(err.message || 'Google sign-up failed. If you already have an account, try signing in.');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const journeySteps = fromHire
    ? ['Enquiry sent', 'Create account', 'Upload documents', 'Request booking']
    : ['Choose studio', 'Create account', 'Upload documents', 'Request booking'];

  const nextText = 'Next: upload onboarding documents so the studio can approve booking access.';
  const selectedOrganisation = organisations.find((org) => String(org.id) === String(formData.organisation_id));
  const lockedEmail = Boolean(handoff.verifiedEmail);
  const hasGoogleSignUp = Boolean(config.oauth.googleClientId)
    || (typeof window !== 'undefined' && Boolean(window.__STUDIOAEYE_TEST_GOOGLE_CLIENT_ID || window.google?.accounts?.id));

  return (
    <div className={`experience-page electric-wayfinding ${isReady ? 'is-ready' : ''}`}>
      <header className="experience-topbar">
        <Link to="/" className="experience-brand-mark">
          <span className="experience-brand-mark__glyph">SA</span>
          <span className="experience-brand-mark__text">StudioAEye Registration</span>
        </Link>

        <nav className="experience-topbar__nav" aria-label="Registration">
          <Link to="/hire">Venue hire</Link>
          <Link to="/login">Login</Link>
        </nav>
      </header>

      <main className="register-scene">
        <section className="register-scene__content reveal-on-scroll is-visible">
          <span className="experience-kicker">Account creation</span>
          <h1>{fromHire ? 'Your enquiry is in. Now create the account that carries it forward.' : 'Create your StudioAEye account and keep the journey moving.'}</h1>
          <p>
            This page stays explicit for first-time users: you can see the current step, what has already happened,
            and what happens next after you create your account.
          </p>

          <SequencePathBar steps={journeySteps} currentStep={1} nextText={nextText} />

          <div className="register-summary-stack">
            {fromHire ? (
              <div className="register-context-card">
                <span className="register-note-card__eyebrow">Context carried forward</span>
                <dl>
                  <div>
                    <dt>Enquiry</dt>
                    <dd>{handoff.enquiryReference || 'Sent from venue hire page'}</dd>
                  </div>
                  <div>
                    <dt>Studio</dt>
                    <dd>{handoff.organisationName || selectedOrganisation?.name || 'Selected during registration'}</dd>
                  </div>
                  <div>
                    <dt>Room</dt>
                    <dd>{handoff.selectedRoomName || 'No room selected'}</dd>
                  </div>
                  <div>
                    <dt>Verified email</dt>
                    <dd>{handoff.verifiedEmail}</dd>
                  </div>
                </dl>
              </div>
            ) : null}

            <div className="register-note-card">
              <span className="register-note-card__eyebrow">What happens next</span>
              <ol>
                <li>Create your account and join the right studio location.</li>
                <li>Upload the onboarding documents the studio requires.</li>
                <li>Wait for approval, then request the booking in the app.</li>
              </ol>
            </div>
          </div>
        </section>

        <section className="register-scene__form reveal-on-scroll is-visible">
          <div className="register-form-card">
            <span className="experience-kicker">Step 2 of 4</span>
            <h2>Create account</h2>
            <p>{fromHire ? 'We have carried forward what we can so you do not need to start over.' : 'Choose your studio, enter your details, and we will take you into the app.'}</p>

            <form onSubmit={handleSubmit} className="register-form-grid">
              <label>
                <span>Full name</span>
                <input
                  id="displayName"
                  name="displayName"
                  type="text"
                  value={formData.displayName}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  className={`register-input ${errors.displayName ? 'has-error' : ''}`}
                  disabled={loading}
                />
                {errors.displayName ? <small>{errors.displayName}</small> : null}
              </label>

              <label>
                <span>Email</span>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  className={`register-input ${errors.email ? 'has-error' : ''}`}
                  autoComplete="email"
                  disabled={loading || lockedEmail}
                  readOnly={lockedEmail}
                />
                {lockedEmail ? <small className="register-help">This verified email was carried over from your enquiry.</small> : null}
                {errors.email ? <small>{errors.email}</small> : null}
              </label>

              <label>
                <span>Studio location</span>
                <select
                  id="organisation_id"
                  name="organisation_id"
                  value={formData.organisation_id}
                  onChange={handleChange}
                  className={`register-select ${errors.organisation_id ? 'has-error' : ''}`}
                  disabled={loading || loadingOrgs}
                >
                  <option value="">{loadingOrgs ? 'Loading locations...' : 'Select a studio location'}</option>
                  {organisations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
                {errors.organisation_id ? <small>{errors.organisation_id}</small> : null}
              </label>

              <label>
                <span>Password</span>
                <div className="password-input-wrapper">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Create a password (min. 8 characters)"
                    className={`register-input ${errors.password ? 'has-error' : ''}`}
                    autoComplete="new-password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
                {errors.password ? <small>{errors.password}</small> : null}
              </label>

              <label>
                <span>Confirm password</span>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm your password"
                  className={`register-input ${errors.confirmPassword ? 'has-error' : ''}`}
                  autoComplete="new-password"
                  disabled={loading}
                />
                {errors.confirmPassword ? <small>{errors.confirmPassword}</small> : null}
              </label>

              {error ? <div className="experience-alert experience-alert--error">{error}</div> : null}
              {successMessage ? (
                <div
                  className="experience-alert experience-alert--info"
                  data-testid={/approval/i.test(successMessage) ? 'oauth-pending-approval-message' : undefined}
                >
                  {successMessage}
                </div>
              ) : null}

              <div className="register-form-card__actions">
                <button type="submit" className="experience-button experience-button--primary" disabled={loading || googleLoading || !isFormValid}>
                  {loading ? 'Creating account...' : 'Create account'}
                </button>
              </div>
            </form>

            {hasGoogleSignUp ? (
              <div className="register-oauth-block" style={{ marginTop: '1.5rem' }}>
                <div className="login-divider" style={{ margin: '1rem 0' }}>
                  <div className="login-divider-line" />
                  <span className="login-divider-text">Or sign up with</span>
                  <div className="login-divider-line" />
                </div>
                <p style={{ fontSize: '0.85rem', opacity: 0.75, marginBottom: '0.75rem' }}>
                  Select a studio location above, then continue with Google — no password needed.
                </p>
                <GoogleIdentityButton
                  onCredential={handleGoogleCredential}
                  disabled={googleLoading || loading || !formData.organisation_id}
                  width={360}
                  shape="pill"
                  text="signup_with"
                  theme="outline"
                  enableOneTap={Boolean(formData.organisation_id)}
                  fallbackLabel="Google sign-up unavailable"
                />
              </div>
            ) : null}

            <div className="register-scene__links">
              <Link to="/login" className="register-link">Sign in to an existing account</Link>
              <Link to={fromHire ? '/hire' : '/schedule'} className="register-link">
                {fromHire ? 'Return to venue hire' : 'View public schedule'}
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Register;
